import type React from 'react';
import { useId } from 'react';

import { FieldSet, FormField, Input, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { NOTICES_OF_CODE_VIOLATIONS } from '../../../constants';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function NoticesOfCodeViolations({ section, setSectionValues }: Props) {
  const radioGroupName = useId();
  const caseNumberId = useId();
  const identificationNumberId = useId();

  function onNoNotices(): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IS_ENABLED]: false,
    });
  }

  function onYesNotices(): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IS_ENABLED]: true,
    });
  }

  function onCaseNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER]: value,
    });
  }

  function onIdentificationNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER]: value,
    });
  }

  function onListingChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.LISTING_OF_CODE_VIOLATION]: value,
    });
  }

  function onCaseNumberSelect(): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED]: false,
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED]: true,
    });
  }

  function onIdentificationNumberSelect(): void {
    setSectionValues({
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED]: true,
      [NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED]: false,
    });
  }

  return (
    <FormField
      required
      label={<Label>In the past 12 months, have you received any notices of code violations?</Label>}
      control={
        <YesAndNoExpansion
          yesHandler={onYesNotices}
          noHandler={onNoNotices}
          yesActive={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IS_ENABLED)}
        >
          <FormField
            required
            label={<Label>Listing of code violation</Label>}
            control={
              <Input
                value={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.LISTING_OF_CODE_VIOLATION)}
                onChange={onListingChange}
              />
            }
          />
          <FieldSet
            required
            css={{ marginTop: 'layout.tighter' }}
            legend={<Legend>What notice number do you have?</Legend>}
          >
            <LabeledControl
              controlId={caseNumberId}
              label={<Label>Case number</Label>}
              control={
                <Radio
                  name={radioGroupName}
                  checked={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED)}
                  onChange={onCaseNumberSelect}
                />
              }
            />
            <LabeledControl
              controlId={identificationNumberId}
              label={<Label>Identification number</Label>}
              control={
                <Radio
                  name={radioGroupName}
                  checked={section.getFieldValue(
                    NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED,
                  )}
                  onChange={onIdentificationNumberSelect}
                />
              }
            />
          </FieldSet>
          {section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER_IS_ENABLED) && (
            <FormField
              required
              css={{ marginTop: 'layout.tighter' }}
              label={<Label>Case number</Label>}
              control={
                <Input
                  value={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.CASE_NUMBER)}
                  onChange={onCaseNumberChange}
                  css={{ marginBottom: 'layout.tighter' }}
                />
              }
            />
          )}
          {section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER_IS_ENABLED) && (
            <FormField
              required
              css={{ marginTop: 'layout.tighter' }}
              label={<Label>Identification number</Label>}
              control={
                <Input
                  value={section.getFieldValue(NOTICES_OF_CODE_VIOLATIONS.FIELD_NAME.IDENTIFICATION_NUMBER)}
                  onChange={onIdentificationNumberChange}
                  css={{ marginBottom: 'layout.tighter' }}
                />
              }
            />
          )}
        </YesAndNoExpansion>
      }
    />
  );
}

NoticesOfCodeViolations.description = 'Chicago specific screen';

export default NoticesOfCodeViolations;
